import { useQuery } from '@tanstack/react-query';
import { get } from '@/api/client';
import { Skeleton } from '@/components/atoms/Skeleton';
import { SEOHead } from '@/components/atoms/SEOHead';
import { TagBadge, type TagCategory } from '@/components/atoms/TagBadge';

/**
 * 태그 탐색 페이지 — Phase 7
 *   /tags — 카테고리별 전체 태그 목록 (각 태그 클릭 시 /tag/:slug 이동)
 */

interface TagItem {
  slug: string;
  name: string;
  description: string | null;
  colorClass: string | null;
  tickerCount: number;
}

interface CategoryGroup {
  category: string;
  tags: TagItem[];
}

interface TagsIndexData {
  categories: CategoryGroup[];
}

const CATEGORY_KO: Record<string, string> = {
  style: '스타일',
  size: '규모',
  sector: '섹터',
  theme: '테마',
  trait: '특성',
  risk: '리스크',
  valuechain: '밸류체인',
};

const CATEGORY_DESC: Record<string, string> = {
  style: '재무 지표 기반 투자 스타일',
  size: '시가총액 규모',
  sector: 'KRX 업종 분류',
  theme: '핵심 사업 테마 (AI 분석)',
  trait: '베타·배당 등 특성',
  risk: '관리종목·적자지속 등 리스크 신호',
  valuechain: '밸류체인 위치 (AI 분석)',
};

export function TagsIndexPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['tags', 'index'],
    queryFn: () => get<TagsIndexData>('/tags'),
    staleTime: 30 * 60_000,
  });

  return (
    <div className="container-base py-10">
      <SEOHead
        title="종목 태그 탐색"
        description="시세공간이 분류한 종목 태그 — 스타일, 규모, 섹터, 테마, 특성, 리스크, 밸류체인 카테고리별로 종목을 탐색하세요."
        canonical="/tags"
      />

      <header className="mb-6">
        <h1 className="text-2xl font-extrabold md:text-3xl">종목 태그 탐색</h1>
        <p className="mt-1 text-sm text-[var(--color-text-sub)]">
          7개 카테고리, 카테고리별 태그를 클릭하면 해당 종목 리스트로 이동합니다.
        </p>
      </header>

      {isLoading || !data ? (
        <div className="space-y-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-40 w-full" />
          ))}
        </div>
      ) : (
        <div className="space-y-6">
          {data.categories.map((g) => (
            <section
              key={g.category}
              className="rounded-2xl border border-[var(--color-border)] bg-white p-5"
            >
              <header className="mb-3">
                <div className="flex items-baseline gap-2">
                  <h2 className="text-lg font-bold">{CATEGORY_KO[g.category] ?? g.category}</h2>
                  <span className="text-xs text-[var(--color-text-sub)]">
                    {g.tags.length}개
                  </span>
                </div>
                {CATEGORY_DESC[g.category] && (
                  <p className="mt-0.5 text-xs text-[var(--color-text-sub)]">
                    {CATEGORY_DESC[g.category]}
                  </p>
                )}
              </header>
              <div className="flex flex-wrap gap-2">
                {g.tags.map((t) => (
                  <TagBadge
                    key={t.slug}
                    slug={t.slug}
                    name={`${t.name} ${t.tickerCount > 0 ? `(${t.tickerCount})` : ''}`.trim()}
                    category={g.category as TagCategory}
                    size="md"
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
